import {authorized} from "./ApiClient";
import {send} from "./http";
import {LicenseViewModel, OrderViewModel, PresignedUrl, ProfileBindingModel, TotpSetup, UserViewModel} from "./models";
import {uploadToPresignedUrl} from "./upload";

// The signed-in user's own account.
export const ProfileApi = {
    get: () => authorized<UserViewModel>('GET', '/user-profile'),

    update: (model: ProfileBindingModel) => authorized<UserViewModel>('PUT', '/user-profile', model),

    changePassword: (password: string, newPassword: string) =>
        authorized<void>('PUT', '/user-profile/change-password', { password, newPassword }),

    // Emails a link (valid 10 minutes) to the new address; the change happens when it's opened.
    // currentPassword: when the API asked for it (reauthentication_required; see components/PasswordConfirmation.tsx).
    changeEmail: (emailAddress: string, currentPassword?: string) =>
        authorized<void>('PUT', '/user-profile/change-email', { emailAddress, ...(currentPassword ? { currentPassword } : {}) }),

    // Uploads the image (see components/avatarImage.ts) to S3, then has the API check it (PNG, JPEG or GIF, at most
    // 512×512 and 10 MB; 400 if not) and make it the avatar, as a still picture without metadata. Progress (0–1) is
    // for the upload.
    setAvatar: async (file: Blob, onProgress?: (fraction: number) => void) => {
        const {uploadToken, upload} = await authorized<{ uploadToken: string, upload: PresignedUrl }>(
            'POST', '/user-profile/avatar', { contentType: file.type, size: file.size });
        await uploadToPresignedUrl(upload, file, onProgress);
        return await authorized<UserViewModel>('PUT', '/user-profile/avatar', { uploadToken });
    },

    removeAvatar: () => authorized<UserViewModel>('DELETE', '/user-profile/avatar'),

    // From the emailed link: no session needed.
    verifyEmail: (token: string) => send<void>('PUT', '/user-profile/verify-email', { token }),

    // Two-factor authentication. Each change asks for the current password (400 when it's wrong).
    beginTotpSetup: (password: string) => authorized<TotpSetup>('POST', '/user-profile/mfa/totp', { password }),

    // Recovery codes come back only when the account had none.
    confirmTotpSetup: (code: string) => authorized<{ recoveryCodes?: string[] }>('PUT', '/user-profile/mfa/totp', { code }),

    disableTotp: (password: string) => authorized<void>('DELETE', '/user-profile/mfa/totp', { password }),

    // Emails a code to the account's address, to prove it arrives before turning email codes on.
    sendEmailMfaCode: (password: string) => authorized<void>('POST', '/user-profile/mfa/email', { password }),

    confirmEmailMfa: (code: string) => authorized<void>('PUT', '/user-profile/mfa/email', { code }),

    disableEmailMfa: (password: string) => authorized<void>('DELETE', '/user-profile/mfa/email', { password }),

    regenerateRecoveryCodes: (password: string) =>
        authorized<{ recoveryCodes: string[] }>('POST', '/user-profile/mfa/recovery-codes', { password }),

    licenses: () => authorized<LicenseViewModel[]>('GET', '/user-profile/license'),

    // Frees a seat on one of the user's licenses. Doesn't refund the yearly activation count.
    freeSeat: (licenseId: string, activationId: string) =>
        authorized<void>('DELETE', `/user-profile/license/${encodeURIComponent(licenseId)}/activation/${encodeURIComponent(activationId)}`),

    // Newest first; not checkouts that were never paid.
    orders: () => authorized<OrderViewModel[]>('GET', '/user-profile/order'),

    // With the license key while it's valid.
    order: (id: string) => authorized<OrderViewModel>('GET', `/user-profile/order/${encodeURIComponent(id)}`)
};
